import {
  DialogTrigger,
  DialogContent,
  Dialog,
} from "@capacit-com/lara-law-shared-ui";
import { FC, useState } from "react";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@capacit-com/lara-law-shared-ui";
import { ReactionMap } from "@/models/workspace/WorkspaceComment";
import { User } from "@/models/users/User";
import { cn } from "@/lib/utils";
import UserAvatar from "@/components/images/UserAvatar";
import { formatUsersName } from "@/utils/format";

type ReactionHeaderProps = {
  reactions: ReactionMap;
  getReactor: (userId: string) => User | undefined;
};

const ReactionHeader: FC<ReactionHeaderProps> = ({ reactions, getReactor }) => {
  const [openReactionsModal, setOpenReactionsModal] = useState(false);
  const getReactionCounts = (reactions: ReactionMap) => {
    // Convert map to array of unified codes
    const reactionEntries = Object.entries(reactions);

    // Count reactions by unified string
    const counts = reactionEntries.reduce(
      (acc, [unified, users]) => {
        const codePoints = unified.split("-").map((hex) => parseInt(hex, 16));
        acc[unified] = {
          count: users.length,
          emoji: String.fromCodePoint(...codePoints),
        };
        return acc;
      },
      {} as Record<string, { count: number; emoji: string }>
    );

    // Priority order for unified codes
    const priorityUnified = ["1f44d", "2764-fe0f", "2705"];

    // Sort all reactions first
    const allReactions = Object.entries(counts).sort(
      ([unifiedA, dataA], [unifiedB, dataB]) => {
        const priorityA = priorityUnified.indexOf(unifiedA);
        const priorityB = priorityUnified.indexOf(unifiedB);

        if (priorityA !== -1 && priorityB !== -1) return priorityA - priorityB;
        if (priorityA !== -1) return -1;
        if (priorityB !== -1) return 1;

        return dataB.count - dataA.count;
      }
    );

    // Then slice for the header display
    const sortedReactions = allReactions.slice(0, 3);

    const totalReactions = Object.values(counts).reduce(
      (sum, data) => sum + data.count,
      0
    );

    return { sortedReactions, allReactions, totalReactions };
  };
  return (
    <Dialog open={openReactionsModal} onOpenChange={setOpenReactionsModal}>
      <DialogTrigger asChild>
        <div
          className="flex items-center gap-2"
          onClick={() => setOpenReactionsModal(true)}
        >
          <div className="h-5 border-l border-border" />
          <div className="flex -space-x-0 items-center cursor-pointer group">
            <div className="flex -space-x-0 items-center">
              {getReactionCounts(reactions).sortedReactions.map(
                ([unified, data], index) => {
                  return (
                    <div
                      className="size-5 ring-2 ring-card bg-card rounded-full flex items-center justify-center"
                      key={unified}
                      style={{
                        zIndex:
                          getReactionCounts(reactions).sortedReactions.length -
                          index,
                      }}
                    >
                      {data.emoji}
                    </div>
                  );
                }
              )}
            </div>
            {getReactionCounts(reactions).totalReactions > 0 && (
              <span className="pl-2 text-sm text-muted-foreground group-hover:underline">
                {`+ ${
                  getReactionCounts(reactions).totalReactions === 1
                    ? "1 reaktion"
                    : `${
                        getReactionCounts(reactions).totalReactions
                      } reaktioner`
                }`}
              </span>
            )}
          </div>
        </div>
      </DialogTrigger>
      <DialogContent
        size="lg"
        className="h-[60vh] overflow-hidden flex flex-col"
      >
        <Tabs
          defaultValue={"all"}
          className="workspace-tabs flex flex-col h-full"
        >
          <TabsList className="search tabs-list flex w-full">
            <TabsTrigger
              value="all"
              className="tab-trigger tenant-search-tab p-1 flex flex-row justify-center items-center gap-x-2 mx-0"
            >
              <span className="font-normal">Alle</span>
              <span className="text-sm font-normal">
                ( {getReactionCounts(reactions).totalReactions} )
              </span>
            </TabsTrigger>
            {getReactionCounts(reactions).allReactions.map(
              ([unified, data]) => (
                <TabsTrigger
                  key={unified}
                  value={unified}
                  className="tab-trigger tenant-search-tab p-1 flex flex-row items-center gap-x-2 px-2 mx-0"
                >
                  <span>{data.emoji}</span>
                  <span className="text-sm font-normal"> ( {data.count} )</span>
                </TabsTrigger>
              )
            )}
          </TabsList>

          <div className="overflow-y-auto grow">
            <TabsContent
              key="all"
              value="all"
              className={cn("flex flex-col gap-2", "data-[state=active]:p-1")}
            >
              {getReactionCounts(reactions).allReactions.flatMap(
                ([unified, data]) => {
                  return reactions[unified]?.map((userId: string) => {
                    const user = getReactor(userId);
                    return (
                      <div
                        key={`${unified}-${userId}`}
                        className="relative flex items-center gap-4 mb-2"
                      >
                        <div className="absolute left-6 -bottom-[4.5px] z-10">
                          {data.emoji}
                        </div>
                        <UserAvatar user={user} avatarClassName="size-8" />
                        <span>{formatUsersName(user)}</span>
                      </div>
                    );
                  });
                }
              )}
            </TabsContent>

            {getReactionCounts(reactions).allReactions.map(
              ([unified, data]) => (
                <TabsContent
                  key={unified}
                  value={unified}
                  className={cn(
                    "flex flex-col gap-2",
                    "data-[state=active]:p-1",
                    "hidden data-[state=active]:block"
                  )}
                >
                  {reactions[unified]?.map((userId: string, index: number) => {
                    const user = getReactor(userId);
                    return (
                      <div
                        key={index}
                        id={index.toString()}
                        className={cn("relative flex items-center gap-4 mb-2")}
                      >
                        <div className="absolute left-6 -bottom-[4.5px] z-10">
                          {data.emoji}
                        </div>
                        <UserAvatar user={user} avatarClassName="size-8" />
                        <span>{formatUsersName(user)}</span>
                      </div>
                    );
                  })}
                </TabsContent>
              )
            )}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
export default ReactionHeader;
